import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BrandWordmark } from "@/components/BrandWordmark";
import { Trophy, LogOut, Users, Calendar, BookOpen, Menu, User, ChevronDown, X, Medal, BarChart3, Swords, Target, Award, LayoutDashboard, Printer } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, auth: true, exact: true },
  { to: "/tournaments", label: "Tournaments", icon: Trophy, auth: true },
  { to: "/bracket", label: "World Cup", icon: Trophy, auth: true },
  { to: "/leaderboard", label: "Leaderboard", icon: Medal, auth: true },
  { to: "/groups", label: "Groups", icon: Users, auth: true, badge: true },
  { to: "/schedule", label: "Schedule", icon: Calendar, guestOnly: true },
  { to: "/blog", label: "Blog", icon: BookOpen, guestOnly: true },
];

const moreLinks = [
  { to: "/schedule", label: "Schedule", icon: Calendar, auth: true },
  { to: "/world-cup-bracket", label: "Summer 2026 Bracket", icon: Printer },
  { to: "/golden-boot", label: "Golden Boot", icon: Award, auth: true },
  { to: "/blog", label: "Blog", icon: BookOpen, auth: true },
];

export function Header() {
  const { user, signOut } = useAuth();
  const { unreadCount } = useUnreadMessages();
  const location = useLocation();
  const [profile, setProfile] = useState<{ display_name: string; avatar_url: string | null } | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) { setProfile(null); return; }
    supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data) setProfile(data);
      });
  }, [user]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // When logged in, the sidebar handles navigation — don't render the header
  if (user) return null;

  const isActive = (path: string, exact?: boolean) => exact ? location.pathname === path : location.pathname === path;
  const visibleLinks = navLinks.filter((l) => (!l.auth || user) && (!l.guestOnly || !user));
  const visibleMoreLinks = moreLinks.filter((l) => !l.auth || user);
  const allMobileLinks = [...visibleLinks, ...visibleMoreLinks] as typeof navLinks;

  return (
    <header className="relative z-50 bg-card/80 backdrop-blur-xl border-b border-border/50 transition-colors duration-300">
      <div className="container mx-auto flex items-center justify-between py-2.5 px-4 md:px-6">
        {/* Logo */}
        <Link to="/" className="shrink-0 hover-scale">
          <BrandWordmark size="md" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-0.5">
          {visibleLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-body font-semibold whitespace-nowrap transition-all duration-200 ${
                isActive(link.to, link.exact)
                  ? "bg-gradient-to-r from-primary/20 via-primary/5 to-transparent text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              <link.icon className="w-4 h-4" />
              {link.label}
              {link.badge && unreadCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </motion.span>
              )}
            </Link>
          ))}
          {visibleMoreLinks.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-body font-semibold whitespace-nowrap transition-all duration-200 ${
                  visibleMoreLinks.some(l => isActive(l.to))
                    ? "bg-gradient-to-r from-primary/20 via-primary/5 to-transparent text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}>
                  More
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {visibleMoreLinks.map((link) => (
                  <DropdownMenuItem key={link.to} asChild>
                    <Link to={link.to} className={`flex items-center gap-2 cursor-pointer ${isActive(link.to) ? "text-primary" : ""}`}>
                      <link.icon className="w-4 h-4" /> {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-1.5">
          {/* Notification bell */}
          {user && <NotificationBell />}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-secondary/60 transition-all duration-200 outline-none">
                  <div className="w-8 h-8 rounded-full bg-primary/20 border-2 border-primary/40 flex items-center justify-center overflow-hidden transition-colors">
                    {profile?.avatar_url ? (
                      <span className="text-lg leading-none">{profile.avatar_url}</span>
                    ) : (
                      <User className="w-4 h-4 text-primary" />
                    )}
                  </div>
                  <span className="hidden sm:block text-sm font-body font-semibold text-foreground max-w-[120px] truncate">
                    {profile?.display_name || user.email?.split("@")[0] || "Player"}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {profile?.display_name || "Player"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/bracket" className="flex items-center gap-2 cursor-pointer">
                    <Trophy className="w-4 h-4" /> My Bracket
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/groups" className="flex items-center gap-2 cursor-pointer">
                    <Users className="w-4 h-4" /> Groups
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                    <User className="w-4 h-4" /> Profile Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/analytics" className="flex items-center gap-2 cursor-pointer">
                    <BarChart3 className="w-4 h-4" /> Analytics
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/compare" className="flex items-center gap-2 cursor-pointer">
                    <Swords className="w-4 h-4" /> Head-to-Head
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/stats" className="flex items-center gap-2 cursor-pointer">
                    <Target className="w-4 h-4" /> My Stats
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={signOut}
                  className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/auth">
                  <Button variant="outline" size="default" className="font-semibold text-sm">
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button size="default" className="font-semibold text-sm gap-1.5">
                    <Trophy className="w-4 h-4" />
                    Create Bracket
                  </Button>
                </Link>
              </div>
              <Link to="/auth" className="sm:hidden">
                <Button size="sm" className="font-semibold text-sm gap-1.5">
                  <Trophy className="w-4 h-4" />
                  Sign Up
                </Button>
              </Link>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-all duration-200"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <AnimatePresence mode="wait" initial={false}>
              {mobileOpen ? (
                <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <X className="w-5 h-5" />
                </motion.div>
              ) : (
                <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                  <Menu className="w-5 h-5" />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Mobile nav dropdown */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-border/50 bg-card/95 backdrop-blur-xl overflow-hidden"
          >
            <nav className="container mx-auto px-4 py-3 flex flex-col gap-1">
              {allMobileLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={`relative flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-body font-semibold transition-all duration-200 ${
                    isActive(link.to, link.exact)
                      ? "bg-gradient-to-r from-primary/20 via-primary/5 to-transparent text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                  {link.badge && unreadCount > 0 && (
                    <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Link>
              ))}
              {!user && (
                <>
                  <Link
                    to="/auth"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-body font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-all duration-200"
                  >
                    <User className="w-4 h-4" />
                    Sign In
                  </Link>
                  <Link
                    to="/auth"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-3 mt-1 rounded-lg text-sm font-bold bg-primary text-primary-foreground justify-center"
                  >
                    <Trophy className="w-4 h-4" />
                    Create Bracket
                  </Link>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
